import { Faq } from '@/molecules';
import { InfoSeller } from '@/organisms';
import { FlexibleContent, HeroSlider } from '@/templates';
import { getBaseLayout } from '@/layouts/BaseLayout';

import { getAllRoutes, getInitialProps } from '@/lib/graphql/api';
import { FlexibleContentBlocks, seo, sliderHero } from '@/lib/graphql/partials';
import { PageQuery } from '@/lib/graphql/querys';
import { pagePropsServerTransformer } from '@/lib/graphql/transformers';
import { routes } from '@/lib/routes';
import { isNumber, toCurrency } from '@/lib/utils';

import type { NextPageWithLayout } from 'pages/_app';

const listFormart = (items: string[]) =>
  new Intl.ListFormat('es', { style: 'short', type: 'disjunction' }).format(
    items.filter(Boolean),
  );

type TypeSeller = {
  description: string;
  estimatedDelivery: string | null;
  shipping: {
    isAlwaysFree: boolean;
    minAmount: string | null;
    price: string | null;
  };
  returnPolicy: any;
  professionalInfo: {
    corporateName: string | null;
    identificationNumber: string | null;
    taxIdentificationNumber: string | null;
  } | null;
};

type TypePageSeller = {
  pageProps: {
    hero: any;
    flexibleContent: any;
    title: string;
    sellersMirakl: TypeSeller;
  };
};

const Seller: NextPageWithLayout<TypePageSeller> = ({
  pageProps: { hero, flexibleContent, title, sellersMirakl },
}) => (
  <>
    <HeroSlider hero={hero} />

    <FlexibleContent flexible={flexibleContent?.flexible} type="Seller" />

    {(sellersMirakl?.estimatedDelivery ||
      sellersMirakl?.shipping?.price ||
      sellersMirakl?.shipping?.isAlwaysFree ||
      sellersMirakl?.shipping?.minAmount ||
      sellersMirakl?.returnPolicy ||
      sellersMirakl.description ||
      sellersMirakl?.professionalInfo) && (
      <InfoSeller
        title={`Sobre ${title}`}
        backgroundColor="#FFF"
        {...(sellersMirakl.description && {
          description: sellersMirakl.description,
        })}
      >
        {(sellersMirakl?.estimatedDelivery ||
          sellersMirakl?.shipping?.price ||
          sellersMirakl?.shipping?.isAlwaysFree ||
          sellersMirakl?.shipping?.minAmount ||
          sellersMirakl?.returnPolicy ||
          sellersMirakl?.professionalInfo) && (
          <div className="flex w-full flex-col gap-2">
            {(sellersMirakl?.estimatedDelivery ||
              sellersMirakl?.shipping?.price ||
              sellersMirakl?.shipping?.isAlwaysFree ||
              sellersMirakl?.shipping?.minAmount) && (
              <Faq question="1. Envíos" open>
                {sellersMirakl.estimatedDelivery && (
                  <p className="!text-typo-primary">
                    Entrega estimada en {sellersMirakl.estimatedDelivery} días.
                  </p>
                )}

                {sellersMirakl.shipping.isAlwaysFree ||
                sellersMirakl.shipping.minAmount ? (
                  <>
                    {sellersMirakl.shipping.price &&
                      !sellersMirakl.shipping.isAlwaysFree && (
                        <p className="!text-typo-primary">
                          Gastos de envío: {sellersMirakl.shipping.price}.
                        </p>
                      )}

                    <p className="!text-typo-primary">
                      Envío gratis
                      {sellersMirakl.shipping.minAmount &&
                      !sellersMirakl.shipping.isAlwaysFree
                        ? ` a partir de ${sellersMirakl.shipping.minAmount}`
                        : ''}
                      .
                    </p>
                  </>
                ) : (
                  sellersMirakl.shipping.price && (
                    <p className="!text-typo-primary">
                      Gastos de envío: {sellersMirakl.shipping.price}.
                    </p>
                  )
                )}
              </Faq>
            )}

            {sellersMirakl?.returnPolicy && (
              <Faq question="2. Política de devoluciones">
                {sellersMirakl.returnPolicy}
              </Faq>
            )}

            {sellersMirakl?.professionalInfo && (
              <Faq question="3. Información de la empresa">
                <>
                  {sellersMirakl?.professionalInfo?.corporateName && (
                    <p>
                      Nombre de la empresa:{' '}
                      {sellersMirakl.professionalInfo.corporateName}
                    </p>
                  )}

                  {sellersMirakl?.professionalInfo.identificationNumber && (
                    <p>
                      CIF / NIF:{' '}
                      {sellersMirakl.professionalInfo.identificationNumber}
                    </p>
                  )}

                  {sellersMirakl?.professionalInfo.taxIdentificationNumber && (
                    <p>
                      Número de IVA intracomunitario:{' '}
                      {sellersMirakl.professionalInfo.taxIdentificationNumber}
                    </p>
                  )}
                </>
              </Faq>
            )}
          </div>
        )}
      </InfoSeller>
    )}
  </>
);

export async function getStaticProps({ params }: any) {
  if (params && Array.isArray(params.slug) && isNumber(params.slug.at(-1)))
    params.slug.pop();

  const content = `
    seller(id: $slug, idType: SLUG) {
      title
      sellersMirakl {
        description
        envio {
          gratuito
          precioMinimo
          precioMaximo
          tiempoEntregaMaximo
          tiempoEntregaMinimo
          transportista {
            pedidoMinimo
          }
        }
        shippings {
          returnPolicy
        }
        contact {
          professionalInfo {
            corporateName
            identificationNumber
            taxIdentificationNumber
          }
        }
      }
      ${seo}
      hero:${sliderHero('Seller')}
      flexibleContent: ${FlexibleContentBlocks('Seller')}
    }
  `;
  const Query = PageQuery(content, '', 'Seller');
  const initialProps = await getInitialProps(Query, {
    slug: params.slug.join('/'),
  });

  if (initialProps.notFound) return initialProps;

  if (initialProps?.props?.pageProps?.seo?.canonical) {
    initialProps.props.pageProps.seo.canonical =
      initialProps.props.pageProps.seo.canonical.replace(
        'miraklsellers',
        'vendedores',
      );
  }

  const staticProps = await pagePropsServerTransformer({
    staticProps: initialProps,
    type: 'Seller',
    path: routes.sellers.seller(params.slug.join('/')),
  });
  const sellerMirakl = staticProps.props.pageProps.sellersMirakl;
  const minAmountArray: number[] = sellerMirakl?.envio?.transportista
    ?.map((item: any) => item.pedidoMinimo)
    .filter(Boolean);
  const seller = {
    description: sellerMirakl.description,
    estimatedDelivery:
      sellerMirakl?.envio?.tiempoEntregaMinimo &&
      sellerMirakl?.envio?.tiempoEntregaMaximo
        ? listFormart([
            sellerMirakl.envio.tiempoEntregaMinimo,
            sellerMirakl.envio.tiempoEntregaMaximo,
          ])
        : null,
    shipping: {
      isAlwaysFree: Boolean(sellerMirakl.envio.gratuito),
      minAmount:
        minAmountArray?.length > 0
          ? toCurrency(Math.min(...minAmountArray))
          : null,
      price:
        (sellerMirakl.envio.precioMinimo !== null &&
          sellerMirakl.envio.precioMinimo !== undefined) ||
        (sellerMirakl.envio.precioMaximo !== null &&
          sellerMirakl.envio.precioMaximo !== undefined)
          ? toCurrency(
              sellerMirakl.envio.precioMinimo ??
                sellerMirakl.envio.precioMaximo,
            )
          : null,
    },
    returnPolicy: sellerMirakl.shippings?.returnPolicy,
    professionalInfo: sellerMirakl.contact.professionalInfo,
  };

  staticProps.props.pageProps.sellersMirakl = seller;

  return staticProps;
}

export async function getStaticPaths() {
  const allProducts = await getAllRoutes({ type: 'sellers' });

  return {
    paths: process.env.FULL_DEPLOY
      ? allProducts.map((item) => item.replace('miraklsellers', 'vendedores'))
      : allProducts
          .slice(0, 1)
          .map((item) => item.replace('miraklsellers', 'vendedores')),
    fallback: 'blocking',
  };
}

Seller.getLayout = getBaseLayout;

export default Seller;
